import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  View as RNView,
  ScrollView,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { Pagination } from './pagination';

interface Props<T = any> {
  data: T[];
  renderItem(data: T): ReactElement;
  itemWidthPercentage?: number;
  initialActiveIndex?: number;
  paginationColor?: string;
  style?: StyleProp<ViewStyle>;
  contentOffset?: Animated.Value;
  showNavButtons?: boolean;
  onChangeActiveIndex?(index: number): void;
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  prevButton: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nextButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  navButton: {
    paddingHorizontal: 24,
  },
  disabled: {
    opacity: 0.25,
    pointerEvents: 'none',
  },
});

export function Carousel<T>({
  data,
  style,
  contentOffset,
  renderItem,
  onChangeActiveIndex,
  paginationColor = 'black',
  itemWidthPercentage = 0.8,
  initialActiveIndex = 0,
  showNavButtons = false,
}: Props<T>) {
  const scrollView = useRef<ScrollView>(null);

  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
  const [width, setWidth] = useState(0);
  const itemWidth = width * itemWidthPercentage;
  const paddingHorizontal = (width - itemWidth) / 2;
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    setWidth(event.nativeEvent.layout.width);
  }, []);

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const centerX = event.nativeEvent.contentOffset.x;
      const index = Math.round(centerX / itemWidth);
      setActiveIndex(index);
      onChangeActiveIndex?.(index);
      contentOffset?.setValue(event.nativeEvent.contentOffset.x);
    },
    [itemWidth, contentOffset, onChangeActiveIndex],
  );

  const scrollToIndex = useCallback(
    (index: number, animated: boolean = true) => {
      scrollView.current?.scrollTo({ animated, x: index * itemWidth });
    },
    [itemWidth],
  );
  const scrollToPrev = useCallback(
    () => scrollToIndex(Math.max(activeIndex - 1, 0)),
    [scrollToIndex, activeIndex],
  );
  const scrollToNext = useCallback(
    () => scrollToIndex(Math.min(activeIndex + 1, data.length - 1)),
    [scrollToIndex, activeIndex, data.length],
  );

  useEffect(() => {
    if (!!width && typeof initialActiveIndex !== 'undefined') {
      scrollToIndex(initialActiveIndex);
    }
  }, [width, initialActiveIndex, scrollToIndex]);

  const contentContainerStyle = useMemo<StyleProp<ViewStyle>>(
    () => ({
      paddingHorizontal,
      width: itemWidth * data.length + paddingHorizontal * 2,
    }),
    [data.length, itemWidth, paddingHorizontal],
  );

  const renderedItems = useMemo<ReactElement[]>(
    () =>
      data.map((item, index) => (
        <View key={index} style={{ width: itemWidth, height: '100%' }}>
          {renderItem(item)}
        </View>
      )),
    [data, itemWidth, renderItem],
  );

  return (
    <RNView onLayout={onLayout} style={style}>
      {!!width && (
        <>
          <ScrollView
            ref={scrollView}
            horizontal
            showsHorizontalScrollIndicator={false}
            decelerationRate="fast"
            contentContainerStyle={contentContainerStyle}
            style={{ width: '100%' }}
            scrollEventThrottle={16}
            onScroll={onScroll}
            pagingEnabled={true}
            // @ts-ignore
            className="carousel-snap-center"
          >
            {renderedItems}
          </ScrollView>
          {data.length > 1 && (
            <>
              {showNavButtons && (
                <RNView style={[styles.prevButton, styles.navButton]}>
                  <Button
                    variant="tertiary"
                    size="large"
                    iconOnly
                    icon="arrow-point-left"
                    onClick={scrollToPrev}
                    disabled={activeIndex === 0}
                  />
                </RNView>
              )}
              {!showNavButtons && (
                <TouchableOpacity
                  onPress={scrollToPrev}
                  style={[
                    styles.prevButton,
                    { width: paddingHorizontal },
                    activeIndex === 0 && styles.disabled,
                  ]}
                />
              )}

              {showNavButtons && (
                <RNView style={[styles.nextButton, styles.navButton]}>
                  <Button
                    variant="tertiary"
                    size="large"
                    iconOnly
                    icon="arrow-point-right"
                    onClick={scrollToNext}
                    disabled={activeIndex === data.length - 1}
                  />
                </RNView>
              )}
              {!showNavButtons && (
                <TouchableOpacity
                  onPress={scrollToNext}
                  style={[
                    styles.nextButton,
                    { width: paddingHorizontal },
                    activeIndex === data.length - 1 && styles.disabled,
                  ]}
                />
              )}
              <Pagination
                color={paginationColor}
                length={data.length}
                activeIndex={activeIndex}
                onPress={scrollToIndex}
              />
            </>
          )}
        </>
      )}
    </RNView>
  );
}
